import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Users } from 'lucide-react';

export const ScheduleBookingModal = ({ isOpen, onClose, onSubmit, isProcessing, currentUser, isAdmin = false }) => {
  const [formData, setFormData] = useState({
    eventName: '',
    eventType: 'Crusade',
    startDate: '',
    endDate: '',
    location: '',
    attendees: '',
    description: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
  });

  const [errors, setErrors] = useState({});

  const eventTypes = [
    'Crusade',
    'Concert',
    'collaboration',
    'workshop',
    'other'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.eventName.trim()) newErrors.eventName = 'Event name is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (formData.endDate && formData.startDate && new Date(formData.endDate) < new Date(formData.startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (formData.attendees && isNaN(formData.attendees)) newErrors.attendees = 'Must be a valid number';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const scheduleData = {
      ...formData,
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      createdAt: new Date().toISOString(),
      status: isAdmin ? 'approved' : 'pending'
    };

    await onSubmit(scheduleData);
    
    setFormData({
      eventName: '',
      eventType: 'performance',
      startDate: '',
      endDate: '',
      location: '',
      attendees: '',
      description: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-[#121318] border border-white/10 rounded-2xl shadow-2xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto text-white font-sans antialiased relative"
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            onClick={e => e.stopPropagation()}
          >
            {/* Ambient Gold Glow */}
            <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#bd9f67]/15 rounded-full blur-2xl pointer-events-none" />

            {/* Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
              <div>
                <div className="flex items-center gap-2 text-[#bd9f67] mb-1">
                  <span className="text-xs font-semibold tracking-wider uppercase">Ministration Request</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white">Book a Schedule</h2>
                <p className="text-white/60 text-xs md:text-sm mt-0.5">Request The AsidorS for an event or ministry night</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Event Details Section */}
            <div className="bg-[#181a22] border border-white/5 rounded-xl p-4 md:p-5 mb-5">
              <h3 className="text-sm font-semibold text-[#bd9f67] mb-4 flex items-center gap-2 uppercase tracking-wider">
                <Calendar size={16} /> Event Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Event Name */}
                <div>
                  <label className="block text-xs font-medium text-white/80 mb-1.5">
                    Event Name *
                  </label>
                  <input
                    type="text"
                    name="eventName"
                    value={formData.eventName}
                    onChange={handleChange}
                    placeholder="e.g., Concert, Worship Night"
                    className={`w-full px-3.5 py-2.5 bg-black/40 border rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition ${
                      errors.eventName ? 'border-red-500/80' : 'border-white/10'
                    }`}
                  />
                  {errors.eventName && <p className="text-red-400 text-xs mt-1">{errors.eventName}</p>}
                </div>

                {/* Event Type */}
                <div>
                  <label className="block text-xs font-medium text-white/80 mb-1.5">
                    Event Type
                  </label>
                  <select
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    className="w-full px-3.5 py-2.5 bg-[#121318] border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#bd9f67] transition"
                  >
                    {eventTypes.map(type => (
                      <option key={type} value={type} className="bg-[#121318] text-white">
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-xs font-medium text-white/80 mb-1.5">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className={`w-full px-3.5 py-2.5 bg-black/40 border rounded-xl text-sm text-white focus:outline-none focus:border-[#bd9f67] transition ${
                      errors.startDate ? 'border-red-500/80' : 'border-white/10'
                    }`}
                  />
                  {errors.startDate && <p className="text-red-400 text-xs mt-1">{errors.startDate}</p>}
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-xs font-medium text-white/80 mb-1.5">
                    End Date *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className={`w-full px-3.5 py-2.5 bg-black/40 border rounded-xl text-sm text-white focus:outline-none focus:border-[#bd9f67] transition ${
                      errors.endDate ? 'border-red-500/80' : 'border-white/10'
                    }`}
                  />
                  {errors.endDate && <p className="text-red-400 text-xs mt-1">{errors.endDate}</p>}
                </div>

                {/* Location */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/80 mb-1.5">
                    Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Event venue or address"
                    className={`w-full px-3.5 py-2.5 bg-black/40 border rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition ${
                      errors.location ? 'border-red-500/80' : 'border-white/10'
                    }`}
                  />
                  {errors.location && <p className="text-red-400 text-xs mt-1">{errors.location}</p>}
                </div>

                {/* Attendees */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/80 mb-1.5">
                    Expected Attendees
                  </label>
                  <input
                    type="number"
                    name="attendees"
                    value={formData.attendees}
                    onChange={handleChange}
                    placeholder="e.g., 100"
                    className={`w-full px-3.5 py-2.5 bg-black/40 border rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition ${
                      errors.attendees ? 'border-red-500/80' : 'border-white/10'
                    }`}
                  />
                  {errors.attendees && <p className="text-red-400 text-xs mt-1">{errors.attendees}</p>}
                </div>
              </div>

              {/* Description */}
              <div className="mt-4">
                <label className="block text-xs font-medium text-white/80 mb-1.5">
                  Event Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Tell us more about your event..."
                  rows={3}
                  className="w-full px-3.5 py-2.5 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition"
                />
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="bg-[#181a22] border border-white/5 rounded-xl p-4 md:p-5">
              <h3 className="text-sm font-semibold text-[#bd9f67] mb-4 flex items-center gap-2 uppercase tracking-wider">
                <Users size={16} /> Contact Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Contact Name */}
                <div>
                  <label className="block text-xs font-medium text-white/80 mb-1.5">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    placeholder="Your full name (optional)"
                    className="w-full px-3.5 py-2.5 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition"
                  />
                </div>

                {/* Contact Email */}
                <div>
                  <label className="block text-xs font-medium text-white/80 mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    placeholder="your@email.com (optional)"
                    className="w-full px-3.5 py-2.5 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition"
                  />
                </div>

                {/* Contact Phone */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/80 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000 (optional)"
                    className="w-full px-3.5 py-2.5 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition"
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-6">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 rounded-xl text-sm font-semibold transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#bd9f67] hover:bg-[#d0b07a] text-gray-950 rounded-xl text-sm font-bold transition shadow-lg shadow-[#bd9f67]/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScheduleBookingModal;